import { http } from '../lib/api/http';
import { getTrackedPageVisitKeys } from '../constants/pageVisits';

const MAX_VISIT_RECORDS_PER_TIMEFRAME = 4000;

export const PAGE_VISIT_TIMEFRAMES = {
  daily: {
    label: 'Últimas 24 horas',
    windowMs: 24 * 60 * 60 * 1000,
  },
  weekly: {
    label: 'Últimos 7 días',
    windowMs: 7 * 24 * 60 * 60 * 1000,
  },
  monthly: {
    label: 'Últimos 30 días',
    windowMs: 30 * 24 * 60 * 60 * 1000,
  },
  yearly: {
    label: 'Últimos 365 días',
    windowMs: 365 * 24 * 60 * 60 * 1000,
  },
};

const normalizeText = (value, maxLength = 255) => {
  if (!value) return '';
  const text = String(value).trim();
  if (!maxLength) return text;
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength);
};

const normalizeDuration = value => {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return null;
  }
  if (value <= 0) return 0;
  const maxDurationMs = 12 * 60 * 60 * 1000; // 12 horas por visita
  return Math.min(Math.round(value), maxDurationMs);
};

const buildPageVisitPayload = ({ pageKey, path, label, user, durationMs }) => {
  const payload = {
    page: pageKey,
    path: normalizeText(path, 512),
    title: normalizeText(label, 120),
    isGuest: true,
    visitorType: 'guest',
  };

  const normalizedDuration = normalizeDuration(durationMs);
  if (normalizedDuration !== null) {
    payload.durationMs = normalizedDuration;
  }

  if (user && typeof user === 'object') {
    const userId = user.id || user.userId || '';
    if (userId) {
      payload.user = userId;
      payload.isGuest = false;
      payload.visitorType = 'registered';
    } else {
      payload.isGuest = true;
      payload.visitorType = 'guest';
    }

    if (user.role) {
      payload.visitorRole = String(user.role);
    }

    if (user.email) {
      payload.visitorEmail = normalizeText(user.email, 160);
    }
  }

  return payload;
};

export async function recordPageVisit({
  pageKey,
  path = '',
  label = '',
  user = null,
  durationMs = null,
} = {}) {
  if (!pageKey) return null;

  try {
    const payload = buildPageVisitPayload({ pageKey, path, label, user, durationMs });
    await http.post('/page-visits', payload);
    return true;
  } catch (error) {
    if (error?.name === 'AbortError') {
      return null;
    }
    console.warn('No se pudo registrar la visita de la página.', error);
    return null;
  }
}

const toNumber = value => {
  if (typeof value !== 'number') return 0;
  if (!Number.isFinite(value)) return 0;
  return value;
};

const computeAverageDuration = (totalDuration, count) => {
  if (!count || count <= 0) return 0;
  const safeTotal = Math.max(toNumber(totalDuration), 0);
  return Math.round(safeTotal / count);
};

const fetchVisitRecords = async ({ pageKey, sinceIso, untilIso }) => {
  const params = {};
  if (pageKey) {
    params.page = pageKey;
  }
  if (sinceIso) {
    params.from = sinceIso;
  }
  if (untilIso) {
    params.to = untilIso;
  }

  const { data } = await http.get('/page-visits', { params });
  const items = Array.isArray(data) ? data : [];
  const truncated = items.length > MAX_VISIT_RECORDS_PER_TIMEFRAME;
  return {
    items: truncated ? items.slice(0, MAX_VISIT_RECORDS_PER_TIMEFRAME) : items,
    truncated,
  };
};

export async function fetchPageVisitSummary({
  pages,
  timeframes,
} = {}) {
  const pageKeys =
    Array.isArray(pages) && pages.length > 0 ? pages.filter(Boolean) : getTrackedPageVisitKeys();

  const timeframeEntries = timeframes
    ? Object.entries(PAGE_VISIT_TIMEFRAMES).filter(([key]) => timeframes.includes(key))
    : Object.entries(PAGE_VISIT_TIMEFRAMES);

  const summary = {};
  const ranges = {};

  const now = new Date();

  timeframeEntries.forEach(([key, config]) => {
    const since = new Date(now.getTime() - config.windowMs);
    summary[key] = {};
    ranges[key] = {
      label: config.label,
      since,
      until: now,
    };
    pageKeys.forEach(pageKey => {
      summary[key][pageKey] = {
        registered: 0,
        guests: 0,
        total: 0,
        durationMsTotal: 0,
        registeredDurationMsTotal: 0,
        guestDurationMsTotal: 0,
        averageDurationMs: 0,
        registeredAverageDurationMs: 0,
        guestAverageDurationMs: 0,
      };
    });
  });

  if (!pageKeys.length) {
    return {
      summary,
      ranges,
      missingCollection: false,
      errors: [],
    };
  }

  const errors = [];
  let missingCollection = false;

  for (const [timeframeKey] of timeframeEntries) {
    const since = ranges[timeframeKey]?.since;
    const until = ranges[timeframeKey]?.until;
    const sinceIso = since ? since.toISOString() : null;
    const untilIso = until ? until.toISOString() : null;

    for (const pageKey of pageKeys) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { items, truncated } = await fetchVisitRecords({ pageKey, sinceIso, untilIso });

        if (truncated) {
          errors.push({
            timeframeKey,
            pageKey,
            message: `El cálculo de ${timeframeKey}/${pageKey} está limitado a los primeros ${MAX_VISIT_RECORDS_PER_TIMEFRAME} registros.`,
            type: 'limit',
          });
        }

        items.forEach(record => {
          const entry = summary[timeframeKey]?.[pageKey];
          if (!entry) return;

          const isGuest =
            typeof record?.is_guest === 'boolean'
              ? record.is_guest
              : Boolean(record?.isGuest);
          const durationMsRaw =
            typeof record?.duration_ms === 'number'
              ? record.duration_ms
              : typeof record?.durationMs === 'number'
              ? record.durationMs
              : 0;
          const durationMs = Number.isFinite(durationMsRaw) ? Math.max(durationMsRaw, 0) : 0;

          if (isGuest) {
            entry.guests += 1;
            entry.guestDurationMsTotal += durationMs;
          } else {
            entry.registered += 1;
            entry.registeredDurationMsTotal += durationMs;
          }

          entry.durationMsTotal += durationMs;
        });
      } catch (error) {
        if (error?.status === 404) {
          missingCollection = true;
          break;
        }
        if (error?.name === 'AbortError') {
          continue;
        }
        errors.push({
          timeframeKey,
          pageKey,
          error,
        });
        console.warn('No se pudo obtener el historial de visitas de página.', error);
      }
    }

    if (missingCollection) {
      break;
    }
  }

  timeframeEntries.forEach(([timeframeKey]) => {
    pageKeys.forEach(pageKey => {
      const entry = summary[timeframeKey][pageKey];
      if (!entry) return;
      entry.total = (entry.registered || 0) + (entry.guests || 0);
      entry.registeredAverageDurationMs = computeAverageDuration(
        entry.registeredDurationMsTotal,
        entry.registered
      );
      entry.guestAverageDurationMs = computeAverageDuration(
        entry.guestDurationMsTotal,
        entry.guests
      );
      entry.averageDurationMs = computeAverageDuration(entry.durationMsTotal, entry.total);
    });
  });

  return {
    summary,
    ranges,
    missingCollection,
    errors,
  };
}
